import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { tenantApi } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { useStaffView } from "@/lib/staff-view";
import { navItems, visibleNavItems, type NavUser } from "@/lib/nav";
import { NetworkBadge } from "@/components/network-status-banner";
import { useState } from "react";
import { Badge, Button, Title, Text, Stack, Group, Box, SimpleGrid, Paper, UnstyledButton } from "@mantine/core";
import { Search } from "lucide-react";
import { FindFreeRoomDialog } from "@/components/find-free-room-dialog";
import { GetStartedCard } from "@/components/guides/get-started-card";
import { useNetworkInfo, useWirelessInfo } from "@/hooks/use-network-queries";
import { useRecentMemberActivity } from "@/hooks/use-member-activity";
import { RecentActivityCard } from "@/components/activity/recent-activity-card";

interface QuickActionProps {
  user: NavUser | null;
  wireless?: { ssid?: string | null } | null;
  quota?: { remaining_amount?: number | null } | null;
  betaEnabled: boolean;
}

function DashboardQuickActions({ user, wireless, quota, betaEnabled }: QuickActionProps) {
  const descriptions: Record<string, string> = {
    "/network": wireless?.ssid || "WiFi & speed test",
    "/print": quota?.remaining_amount != null ? `${quota.remaining_amount} remaining` : "History & setup",
    "/meeting-rooms": "Availability",
    "/faq": "Help & guides",
    "/invoices": "Amounts, due dates & PDFs",
    "/activity": "Alarms & company events",
    "/settings": "Slack, calendars & more",
  };

  const items = visibleNavItems(navItems, user, betaEnabled).filter((item) => item.to !== "/");

  return (
    <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
      {items.map(({ to, label, icon: Icon }) => (
        <UnstyledButton
          key={to}
          component={Link}
          to={to}
          style={{
            textDecoration: "none",
            borderRadius: "var(--mantine-radius-md)",
            transition: "transform 150ms ease, box-shadow 150ms ease",
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--mantine-shadow-md)";
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <Paper withBorder p="lg" radius="md" h="100%">
            <Stack align="center" gap="sm" ta="center">
              <Box
                w={56}
                h={56}
                style={{
                  borderRadius: "var(--mantine-radius-md)",
                  backgroundColor: "var(--mantine-primary-color-light)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  style={{
                    width: 32,
                    height: 32,
                    color: "var(--mantine-primary-color-filled)",
                  }}
                />
              </Box>
              <Box>
                <Text fw={600} fz="sm">
                  {label}
                </Text>
                <Text fz="xs" c="dimmed" mt={2}>
                  {descriptions[to] ?? ""}
                </Text>
              </Box>
            </Stack>
          </Paper>
        </UnstyledButton>
      ))}
    </SimpleGrid>
  );
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const viewAsMember = useStaffView((s) => s.viewAsMember);
  const staffSeesAll = !!user?.is_staff && !viewAsMember;
  // Beta nav items: staff god-mode or the member's own beta-program opt-in (Account page).
  const betaEnabled = staffSeesAll || !!user?.is_beta_tester;

  const { data: quotaData } = useQuery({
    queryKey: ["print-quota"],
    queryFn: () => tenantApi.getPrintQuota(),
    enabled: !!user?.print_available,
  });

  const { data: network } = useNetworkInfo();
  const { data: wireless } = useWirelessInfo();

  const quota = quotaData?.data;

  const {
    data: recentActivity,
    isLoading: recentActivityLoading,
    isError: recentActivityError,
  } = useRecentMemberActivity();

  const [findFreeOpen, setFindFreeOpen] = useState(false);
  return (
    <Stack p="md" gap="lg">
      {/* Welcome + Network Status */}
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Box>
          <Title order={2}>
            Welcome back{user?.first_name ? `, ${user.first_name}` : ""}
          </Title>
          <Text c="dimmed" fz="sm" mt={4}>
            {[user?.tenant_name, user?.site_name].filter(Boolean).join(" \u2014 ") || "Member Portal"}
          </Text>
        </Box>
        <Group gap="xs" mt={4}>
          <NetworkBadge />
          {network && (
            <Badge variant="light" size="sm">
              VLAN {network.vlan_id}
            </Badge>
          )}
        </Group>
      </Group>

      {/* Find a free meeting room */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Box>
            <Text fw={600}>Need a meeting room right now?</Text>
            <Text fz="sm" c="dimmed">Find one that's free and book in two taps.</Text>
          </Box>
          <Button leftSection={<Search size={16} />} onClick={() => setFindFreeOpen(true)}>
            Find a free meeting room
          </Button>
        </Group>
      </Paper>

      <FindFreeRoomDialog opened={findFreeOpen} onClose={() => setFindFreeOpen(false)} />

      {/* Unfinished setup guides (#436); renders nothing when there are none */}
      <GetStartedCard />

      {/* Quick Actions Grid */}
      <DashboardQuickActions
        user={user}
        wireless={wireless}
        quota={quota}
        betaEnabled={betaEnabled}
      />

      <RecentActivityCard
        events={recentActivity}
        isLoading={recentActivityLoading}
        error={recentActivityError}
      />

    </Stack>
  );
}
